import {CopyToClipboardIconButton} from '@github-ui/copy-to-clipboard/IconButton'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {noop} from '@github-ui/noop'
import {CopyIcon, KebabHorizontalIcon, XIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu, IconButton} from '@primer/react'
import {clsx} from 'clsx'
import {Suspense, useRef} from 'react'

import {BUTTON_LABELS} from '../../constants/buttons'
import type {OptionConfig} from '../OptionConfig'
import {CreateIssueButton} from '../preload-components'
import {ActivityPanelToggle} from './ActivityPanelToggle'
import {CreateIssueButtonShell} from './CreateIssueButtonShell'
import type {HeaderMenuIssueNode, HeaderMenuRepositoryNode} from './header-menu-query-types'
import styles from './HeaderMenu.module.css'

export type HeaderMenuBaseProps = {
  optionConfig: OptionConfig
  showActivityTab?: boolean
  setIsIssueTitleEditActive?: (isActive: boolean) => void
  issueTitle?: string
}

export type HeaderMenuFragment = Pick<HeaderMenuIssueNode, 'number' | 'url'> & {
  readonly repository: Pick<HeaderMenuRepositoryNode, 'name' | 'isArchived' | 'isWritable'> & {
    readonly owner: Pick<HeaderMenuRepositoryNode['owner'], 'login'>
  }
}

type HeaderMenuProps = {
  headerMenuData: HeaderMenuFragment
} & HeaderMenuBaseProps

export function HeaderMenu({
  headerMenuData: {url, repository},
  showActivityTab = false,
  optionConfig: {
    customEditMenuEntries,
    navigate,
    showIssueCreateButton,
    additionalHeaderActions,
    onClose,
    useViewportQueries,
    insideSidePanel,
  },
}: HeaderMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  // When the sticky header is enabled, the open-in-agent, copy-link, and create-issue actions render in the sticky top container instead.
  const showInlineActions = !stickyHeaderEnabled
  const showCreateIssueButton = showIssueCreateButton && repository.isWritable && !stickyHeaderEnabled
  // In a side sheet with the sticky header on, the pin/expand, more-actions kebab, and close
  // controls are relocated into the sticky top bar (see StickyHeader). Suppress them here so
  // they don't render twice.
  const relocateSidePanelNav = stickyHeaderEnabled && !!insideSidePanel

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      <div
        className={clsx(
          styles.buttonContainer,
          useViewportQueries ? styles.buttonContainer_viewport : styles.buttonContainer_container,
        )}
      >
        {showCreateIssueButton && (
          <Suspense fallback={<CreateIssueButtonShell />}>
            <CreateIssueButton
              label={BUTTON_LABELS.new}
              navigate={navigate}
              optionConfig={{
                showRepositoryPicker: false,
                issueCreateArguments: {
                  repository: {
                    owner: repository.owner.login,
                    name: repository.name,
                  },
                },
                showFullScreenButton: true,
                // Only navigate if we're in the Repo#Index (ie, have a scoped repository)
                navigateToFullScreenOnTemplateChoice: navigate !== noop && repository !== null,
                canBypassTemplateSelection: true,
                navigate,
              }}
            />
          </Suspense>
        )}
      </div>
      {showInlineActions && (
        <CopyToClipboardIconButton
          textToCopy={url}
          ariaLabel={BUTTON_LABELS.copyIssueLink}
          icon={CopyIcon}
          tooltipProps={{direction: 's'}}
        />
      )}
      {showInlineActions && showActivityTab && <ActivityPanelToggle />}
      {!relocateSidePanelNav && additionalHeaderActions}
      {!relocateSidePanelNav && customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              ref={anchorRef}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!relocateSidePanelNav && onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}

export function StickyHeaderMenu({
  headerMenuData: {url},
  showActivityTab = false,
  optionConfig: {customEditMenuEntries, additionalHeaderActions, onClose, insideSidePanel},
}: HeaderMenuProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)

  const stickyHeaderEnabled = isFeatureEnabled('issues_sticky_header')
  // See HeaderMenu: in a side sheet with the sticky header on, these controls live in the
  // sticky top bar, so suppress them here to avoid duplicates.
  const relocateSidePanelNav = stickyHeaderEnabled && !!insideSidePanel

  return (
    <div className={clsx(styles.menuActionsContainer, stickyHeaderEnabled && styles.menuActionsContainer_stickyHeader)}>
      <CopyToClipboardIconButton
        textToCopy={url}
        ariaLabel={BUTTON_LABELS.copyIssueLink}
        icon={CopyIcon}
        tooltipProps={{direction: 's'}}
      />
      {showActivityTab && <ActivityPanelToggle />}
      {!relocateSidePanelNav && additionalHeaderActions}
      {!relocateSidePanelNav && customEditMenuEntries && (
        <ActionMenu anchorRef={anchorRef}>
          <ActionMenu.Anchor>
            <IconButton
              variant="invisible"
              icon={KebabHorizontalIcon}
              aria-label={BUTTON_LABELS.issueActions}
              tooltipDirection="n"
              ref={anchorRef}
              className={styles.kebabMenuButton}
            />
          </ActionMenu.Anchor>
          <ActionMenu.Overlay>
            <ActionList>{customEditMenuEntries.map(e => e)}</ActionList>
          </ActionMenu.Overlay>
        </ActionMenu>
      )}
      {!relocateSidePanelNav && onClose && (
        <IconButton variant="invisible" aria-label={BUTTON_LABELS.closePanel} icon={XIcon} onClick={onClose} />
      )}
    </div>
  )
}
